import { Store } from '@/core/flux.service';
import { ColumnRulesWithMetaData } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import {
  TABLE_DEFINITION_COLUMN_OPTIONS,
  TABLE_DEFINITION_TEMPLATE_OPTIONS,
} from '@/tableDefinitionEditor/tableEditorOptions.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { CombinedColumnRuleInputParameters } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import type { ColumnRuleWithMetadata } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import type {
  AutoOpenColumnRule,
  ControlPaneDisplay,
  TableDefinitionColumnOptions,
  TableDefinitionTemplateOptions,
} from '@/tableDefinitionEditor/tableDefinition.types';

export interface BaseTableEditorStoreState {
  tableId: string;
  showConfigureSearch: boolean;
  columnToEditId?: string;
  showColumnProperties: boolean;
  enableTreeCreation: boolean;
  selectedColumnOption?: TableDefinitionColumnOptions;
  selectedTemplateOption?: TableDefinitionTemplateOptions;
  visibleControlPane: ControlPaneDisplay;
  shortcutSourceColumnId?: string;
  initialColumnRule?: ColumnRuleWithMetadata;
  initialRuleParameterOverrides?: Partial<CombinedColumnRuleInputParameters>;
  autoOpenColumnRule?: AutoOpenColumnRule;
  restrictColumnMoving: any; // any because of Baobab's `monkey()`;
  storedTableSearchWarningDismissed: boolean;
}

export type BaseTableEditorDehydratedStoreState = Omit<BaseTableEditorStoreState, 'restrictColumnMoving'>;

/**
 * Base class store for the table definition editor
 * Table definition state is not stored in worksteps, so we separate the logic of what table is being looked at
 * here, which is stored in the workstep, while the table definition state is stored in the table definition store
 * which is updated when we receive messages from the server on the socket for table definitions.
 * This base class contains all handler functions, getters and state management that can be used in other editor stores
 */
export abstract class BaseTableEditorStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  initialize() {
    const initialState: BaseTableEditorStoreState = {
      tableId: '',
      showConfigureSearch: false,
      columnToEditId: undefined,
      showColumnProperties: false,
      enableTreeCreation: false,
      selectedColumnOption: undefined,
      selectedTemplateOption: undefined,
      visibleControlPane: 'column',
      shortcutSourceColumnId: undefined,
      initialColumnRule: undefined,
      initialRuleParameterOverrides: undefined,
      autoOpenColumnRule: undefined,
      restrictColumnMoving: this.monkey(
        ['selectedColumnOption'],
        ['selectedTemplateOption'],
        ['columnToEditId'],
        (
          selectedColumnOption: TableDefinitionColumnOptions | undefined,
          selectedTemplateOption: TableDefinitionTemplateOptions | undefined,
          columnToEditId: string | undefined,
        ): boolean => {
          const isEditColumnAction =
            selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.EDIT_COLUMN.value && !!columnToEditId;
          const isAddColumnAction = selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.ADD_COLUMN.value;
          const isAddPropertyToColumn =
            selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.ADD_PROPERTY_TO_COLUMN.value;
          const isAddOrModifyColumn = isEditColumnAction || isAddColumnAction || isAddPropertyToColumn;
          const isEditTemplate =
            selectedTemplateOption?.value === TABLE_DEFINITION_TEMPLATE_OPTIONS.EDIT_TEMPLATE.value;
          const isAddTemplate =
            selectedTemplateOption?.value === TABLE_DEFINITION_TEMPLATE_OPTIONS.CREATE_TEMPLATE.value;
          const isApplyTemplate =
            selectedTemplateOption?.value === TABLE_DEFINITION_TEMPLATE_OPTIONS.APPLY_TEMPLATE.value;
          const isAddOrModifyTemplate = isAddTemplate || isEditTemplate || isApplyTemplate;
          return isAddOrModifyTemplate || isAddOrModifyColumn;
        },
      ),
      storedTableSearchWarningDismissed: false,
    };
    this.state = this.immutable(initialState);
  }

  get tableId(): string {
    return this.state.get('tableId');
  }

  get showConfigureSearch(): boolean {
    return this.state.get('showConfigureSearch');
  }

  get restrictColumnMoving(): boolean {
    return this.state.get('restrictColumnMoving');
  }

  get columnToEditId(): string | undefined {
    return this.state.get('columnToEditId');
  }

  get showColumnProperties(): boolean {
    return this.state.get('showColumnProperties');
  }

  get enableTreeCreation(): boolean {
    return this.state.get('enableTreeCreation');
  }

  get selectedColumnOption(): TableDefinitionColumnOptions | undefined {
    return this.state.get('selectedColumnOption');
  }

  get selectedTemplateOption(): TableDefinitionTemplateOptions | undefined {
    return this.state.get('selectedTemplateOption');
  }

  get visibleControlPane(): ControlPaneDisplay {
    return this.state.get('visibleControlPane');
  }

  get shortcutSourceColumnId(): string | undefined {
    return this.state.get('shortcutSourceColumnId');
  }

  get initialColumnRule(): ColumnRuleWithMetadata | undefined {
    const storedRule = this.state.get('initialColumnRule');
    if (!storedRule) {
      return undefined;
    }
    return ColumnRulesWithMetaData.find((rule) => rule.rule === storedRule.rule);
  }

  get initialRuleParameterOverrides(): Partial<CombinedColumnRuleInputParameters> | undefined {
    return this.state.get('initialRuleParameterOverrides');
  }

  get autoOpenColumnRule(): AutoOpenColumnRule | undefined {
    return this.state.get('autoOpenColumnRule');
  }

  get storedTableSearchWarningDismissed(): boolean {
    return this.state.get('storedTableSearchWarningDismissed');
  }

  protected readonly handlers = {};

  setTableId = (payload: { tableId: string }) => {
    this.state.set('tableId', payload.tableId);
  };

  setShowConfigureSearch = (payload: { showConfigureSearch: boolean }) => {
    this.state.set('showConfigureSearch', payload.showConfigureSearch);
  };

  setColumnToEditId = (payload: { columnToEditId: string | undefined }) => {
    this.state.set('columnToEditId', payload.columnToEditId);
  };

  setShowColumnProperties = (payload: { showColumnProperties: boolean }) => {
    this.state.set('showColumnProperties', payload.showColumnProperties);
  };

  setEnableTreeCreation = ({ enableTreeCreation }: { enableTreeCreation: boolean }) => {
    this.state.set('enableTreeCreation', enableTreeCreation);
  };

  setSelectedColumnOption = ({
    selectedColumnOption,
  }: {
    selectedColumnOption: TableDefinitionColumnOptions | undefined;
  }) => {
    this.state.set('selectedColumnOption', selectedColumnOption);
  };

  setSelectedTemplateOption = ({
    selectedTemplateOption,
  }: {
    selectedTemplateOption: TableDefinitionTemplateOptions | undefined;
  }) => {
    this.state.set('selectedTemplateOption', selectedTemplateOption);
  };

  setShortcutSourceColumnId = (payload: { shortcutSourceColumnId: string | undefined }) => {
    this.state.set('shortcutSourceColumnId', payload.shortcutSourceColumnId);
  };

  setControlPane = (payload: { controlPane: ControlPaneDisplay }) => {
    this.state.set('visibleControlPane', payload.controlPane);
  };

  setInitialColumnRule = (payload: { initialColumnRule: ColumnRuleWithMetadata | undefined }) => {
    this.state.set('initialColumnRule', payload.initialColumnRule);
  };

  setInitialRuleParameterOverrides = (payload: {
    initialRuleParameterOverrides: Partial<CombinedColumnRuleInputParameters> | undefined;
  }) => {
    this.state.set('initialRuleParameterOverrides', payload.initialRuleParameterOverrides);
  };

  setAutoOpenColumnRule = (payload: { autoOpenColumnRule: AutoOpenColumnRule | undefined }) => {
    this.state.set('autoOpenColumnRule', payload.autoOpenColumnRule);
  };

  setStoredTableSearchWarningDismissed = (payload: { dismissed: boolean }) => {
    this.state.set('storedTableSearchWarningDismissed', payload.dismissed);
  };

  reset = () => {
    this.initialize();
  };

  /**
   * Dehydrates the item by retrieving the current set parameters in view
   *
   * @returns An object with the state properties as JSON
   */
  dehydrate(): BaseTableEditorDehydratedStoreState {
    return this.state.serialize();
  }

  /**
   * Rehydrates item from dehydrated state
   *
   * @param dehydratedState - State object that should be restored
   */
  rehydrate(dehydratedState: Partial<BaseTableEditorStoreState>) {
    this.state.merge(dehydratedState);
  }
}
